import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Button, Grid } from '@material-ui/core';

import AppSnackbar from '../../common/AppSnackbar';
import ConfirmDialog from '../../common/dialogs/ConfirmDialog';
import PageTemplate from '../../common/page-template/PageTemplate';
import PageTemplateDashboard from '../../common/page-template/PageTemplateDashboard';
import PageTemplateHeader, {
  PageTemplateHeaderActions
} from '../../common/page-template/PageTemplateHeader';
import { UserContext } from '../../user/User.context';
import { usePatchUser } from '../../user/User.hooks';
import { Role, User } from '../../user/User.interface';
import TrainingChartCard from '../training/cards/TrainingChartCard';
import TrainingLogCard from '../training/cards/TrainingLogCard';
import UserAttachmentCard from './components/UserAttachmentCard';
import UserGroupCard from './components/UserGroupCard';
import UserInfoCard from './components/UserInfoCard';
import UserInviteCard from './components/UserInviteCard';
import UserRoleCard from './components/UserRoleCard';

const StyledGrid = styled(Grid)`
  flex: 1;
  .grid-flex,
  .card-flex,
  .card-content-flex {
    flex: 1;
    display: flex;
    flex-direction: column;
  }
`;

const UserDetailPage: React.FC = () => {
  const { userId: paramUserId = '' } = useParams<{ userId?: string }>();
  const userId: number = parseInt(paramUserId) || 0;
  const [user, setUser] = useState<User>();
  const [open, setOpen] = useState<boolean>(false);
  const [didUpdate, setDidUpdate] = useState<boolean>(false);
  const { user: loggedUser } = useContext(UserContext);
  const { user: updatedUser, isLoading, error, patch } = usePatchUser(userId);

  const canActivate: boolean = useMemo(() => {
    return !!loggedUser?.roles?.some((role: Role) => {
      return role.name && ['Admin', 'SiteAdmin'].includes(role.name);
    });
  }, [loggedUser]);

  useEffect(() => {
    if (updatedUser) {
      setDidUpdate(true);
      setUser({ ...user, ...updatedUser });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedUser]);

  return (
    <PageTemplate>
      <PageTemplateHeader>
        <h2>User Detail</h2>
        <div style={{ flex: 1 }} />
        <PageTemplateHeaderActions>
          {typeof user?.deleted === 'boolean' && (
            <Button
              color='primary'
              variant='contained'
              disabled={isLoading || !canActivate}
              onClick={() => setOpen(true)}
            >
              {user.deleted
                ? 'Activate User Account'
                : 'Deactivate User Account'}
            </Button>
          )}
        </PageTemplateHeaderActions>
      </PageTemplateHeader>
      <PageTemplateDashboard>
        <StyledGrid container spacing={3}>
          <Grid item xs={12} md={8} className='grid-flex'>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <UserInfoCard
                  userId={+(userId || 0)}
                  onChange={user => setUser(user)}
                />
              </Grid>
              <Grid item xs={12}>
                <TrainingLogCard userId={+(userId || 0)} />
              </Grid>
              <Grid item xs={12}>
                <UserAttachmentCard
                  isLoading={isLoading}
                  attachments={user?.attachments || []}
                  onChange={attachments => patch({ attachments })}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={4}>
            <Grid container spacing={3}>
              {user && (
                <Grid item xs={12}>
                  <UserInviteCard user={user} />
                </Grid>
              )}
              <Grid item xs={12}>
                <TrainingChartCard userId={+(userId || 0)} />
              </Grid>
              <Grid item xs={12}>
                <UserGroupCard userId={+(userId || 0)} />
              </Grid>
              <Grid item xs={12}>
                <UserRoleCard userId={+(userId || 0)} />
              </Grid>
            </Grid>
          </Grid>
        </StyledGrid>
      </PageTemplateDashboard>

      <ConfirmDialog
        open={open && !!user}
        title={
          user?.deleted ? 'Activate User Account' : 'Deactivate User Account'
        }
        message={`Are you sure you want to ${
          user?.deleted ? 'activate' : 'deactivate'
        } this account?`}
        onCancel={() => setOpen(false)}
        onConfirm={() => {
          setOpen(false);
          patch({ deleted: !user?.deleted });
        }}
      />

      <AppSnackbar severity='error' open={!!error}>
        {error?.message}
      </AppSnackbar>
      <AppSnackbar
        severity='success'
        open={didUpdate}
        onClose={() => setDidUpdate(false)}
      >
        Employee details updated.
      </AppSnackbar>
    </PageTemplate>
  );
};

export default UserDetailPage;
